import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { Apartment } from '@/hooks/useApartmentDetail';
import type { RentPayment } from '@/hooks/useRentPayments';
import { computeUpcomingRentSchedule, type RentScheduleEntry } from '@/lib/utils/fixed-asset-utils';

interface UpcomingRentScheduleProps {
  apartment: Apartment;
  rentPayments: RentPayment[];
}

function formatDueDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function StatusBadge({ status }: { status: RentScheduleEntry['status'] }) {
  if (status === 'paid') {
    return (
      <Badge className="bg-green-100 text-green-800 hover:bg-green-100" data-testid="status-paid">
        Paid
      </Badge>
    );
  }
  if (status === 'overdue') {
    return (
      <Badge className="bg-red-100 text-red-800 hover:bg-red-100" data-testid="status-overdue">
        Overdue
      </Badge>
    );
  }
  return (
    <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100" data-testid="status-upcoming">
      Upcoming
    </Badge>
  );
}

export function UpcomingRentSchedule({ apartment, rentPayments }: UpcomingRentScheduleProps) {
  if (!apartment.is_occupied) {
    return null;
  }

  const schedule = computeUpcomingRentSchedule(
    apartment.rent_day_of_month,
    rentPayments
  );

  return (
    <Card data-testid="upcoming-rent-schedule">
      <CardHeader>
        <CardTitle>Upcoming Rent Schedule</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Due Date</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {schedule.map((entry, index) => (
              <TableRow key={index} data-testid="rent-schedule-row">
                <TableCell>{formatDueDate(entry.dueDate)}</TableCell>
                <TableCell>
                  <StatusBadge status={entry.status} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
